var _a, _b;
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { PlanTypeCategory } from '../../enum';
import { useReportAPI } from '../report/selectors';
import { useSeatCapacity } from '../seatCapacity';
import { METRICS } from './common/constants';
import { PROD_HOURS_LABEL_INDEX } from './constants';
import { useDailyContext } from './context';
import { isDailyQueueData, useDailyAPI, useDaysOfWeek } from './selectors';
var SINGLE_SKILL_SEATS_METRIC_IDX = (_b = (_a = METRICS.find(function (m) {
  return m.key === 'seatsRequired';
})) === null || _a === void 0 ? void 0 : _a.idx) !== null && _b !== void 0 ? _b : 8;
/**
 * Per-day / per-interval seat-exceed checks for the Daily and Interval views. A day or interval is
 * exceeded when any location's seats there exceed that month's available inventory
 * (capacity − committed) — the per-period-precise comparison, using the same per-location detail
 * the tables render. The active scenario comes from the daily context's `uniqueId` (`planId-...`).
 */
export var useDailySeatExceeded = function () {
  var dailyData = useDailyAPI()[0];
  var state = useDailyContext()[0];
  var reportData = useReportAPI()[0].data;
  var daysOfWeek = useDaysOfWeek();
  var getAvailable = useSeatCapacity().getAvailable;
  var planId = (state === null || state === void 0 ? void 0 : state.uniqueId) ? Number(state.uniqueId.split('-')[0]) : undefined;
  var seatsMetricIdx = (reportData === null || reportData === void 0 ? void 0 : reportData.planTypeCategory) === PlanTypeCategory.ProdHours ? PROD_HOURS_LABEL_INDEX.seatsRequired : SINGLE_SKILL_SEATS_METRIC_IDX;
  var data = dailyData.data;
  var countries = useMemo(function () {
    var _a;
    return isDailyQueueData(data) ? (_a = data.countries) !== null && _a !== void 0 ? _a : [] : [];
  }, [data]);
  var dayYearMonth = useCallback(function (dayIndex) {
    var _a;
    var id = (_a = daysOfWeek[dayIndex]) === null || _a === void 0 ? void 0 : _a.id; // YYYY-MM-DD
    if (!id) return undefined;
    var m = moment.utc(id);
    return {
      year: m.year(),
      month: m.month() + 1
    };
  }, [daysOfWeek]);
  var anyLocationExceeds = useCallback(function (dayIndex, timeKey) {
    if (planId == null || !Number.isFinite(planId)) return false;
    var ym = dayYearMonth(dayIndex);
    if (!ym) return false;
    return countries.some(function (country) {
      var _a;
      return (_a = country.locations) === null || _a === void 0 ? void 0 : _a.some(function (loc) {
        var _a, _b, _c;
        if (!loc.locationName) return false;
        var seats = Number((_c = (_b = (_a = loc.detail) === null || _a === void 0 ? void 0 : _a[dayIndex]) === null || _b === void 0 ? void 0 : _b[timeKey]) === null || _c === void 0 ? void 0 : _c[seatsMetricIdx]);
        if (!Number.isFinite(seats)) return false;
        var available = getAvailable(planId, loc.locationName, ym.year, ym.month);
        return available !== undefined && seats > available;
      });
    });
  }, [countries, dayYearMonth, getAvailable, planId, seatsMetricIdx]);
  var isDayExceeded = useCallback(function (dayIndex) {
    return anyLocationExceeds(dayIndex, 'total');
  }, [anyLocationExceeds]);
  var isIntervalExceeded = useCallback(function (dayIndex, time) {
    return anyLocationExceeds(dayIndex, time);
  }, [anyLocationExceeds]);
  return {
    isDayExceeded: isDayExceeded,
    isIntervalExceeded: isIntervalExceeded
  };
};